import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight,
  CheckCircle,
  AlertCircle,
  Wallet,
  RefreshCw,
  Users,
  Building2,
} from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { formatCurrency, cn } from '../../lib/utils';
import {
  CryptoQuotation,
  CryptoWallet,
  useCreateSale,
} from '../../hooks/useCrypto';

interface CryptoSellModalProps {
  isOpen: boolean;
  onClose: () => void;
  virtualAccountId: string;
  wallets: CryptoWallet[];
  quotations: CryptoQuotation[];
}

type Step = 'type' | 'amount' | 'confirm' | 'success';
type SaleType = 'PLATFORM' | 'P2P';

const SPREAD_PERCENTAGE = 2; // 2% spread for platform sales

export function CryptoSellModal({ isOpen, onClose, virtualAccountId, wallets, quotations }: CryptoSellModalProps) {
  const [step, setStep] = useState<Step>('type');
  const [saleType, setSaleType] = useState<SaleType>('PLATFORM');
  const [selectedWallet, setSelectedWallet] = useState<string>('');
  const [amountCrypto, setAmountCrypto] = useState('');
  const [pricePerUnit, setPricePerUnit] = useState('');
  const [saleResult, setSaleResult] = useState<any>(null);

  const createSale = useCreateSale(virtualAccountId);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setStep('type');
      setSaleType('PLATFORM');
      setSelectedWallet('');
      setAmountCrypto('');
      setPricePerUnit('');
      setSaleResult(null);
      createSale.reset();
    }
  }, [isOpen]);

  const selectedWalletData = wallets.find(w => w.id === selectedWallet);
  const currentQuote = selectedWalletData 
    ? quotations.find(q => (q.symbol || q.currency) === selectedWalletData.network.split('_')[0] || (q.symbol || q.currency) === 'USDT')
    : null;

  // Calculate estimated BRL
  const estimatedBrl = currentQuote && amountCrypto
    ? parseFloat(amountCrypto) * currentQuote.priceBrl * (1 - SPREAD_PERCENTAGE / 100)
    : 0;

  // For P2P, use custom price
  const p2pTotalBrl = pricePerUnit && amountCrypto
    ? parseFloat(amountCrypto) * parseFloat(pricePerUnit)
    : 0;

  const handleConfirmSale = async () => {
    if (!selectedWalletData || !amountCrypto) return;

    try {
      const result = await createSale.mutateAsync({
        type: saleType,
        network: selectedWalletData.network,
        cryptoSymbol: selectedWalletData.network.split('_')[0] || selectedWalletData.network,
        amountCrypto: parseFloat(amountCrypto),
        pricePerUnit: saleType === 'P2P' ? parseFloat(pricePerUnit) : undefined,
      });
      setSaleResult(result);
      setStep('success');
    } catch (error) {
      console.error('Error creating sale:', error);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Vender Criptomoeda" size="lg">
      <AnimatePresence mode="wait">
        {/* Step 1: Select Sale Type */}
        {step === 'type' && (
          <motion.div
            key="type"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <p className="text-surface-400">
              Escolha como deseja vender suas criptomoedas:
            </p>

            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => setSaleType('PLATFORM')}
                className={cn(
                  'p-6 rounded-xl border text-left transition-all',
                  saleType === 'PLATFORM'
                    ? 'border-primary-500 bg-primary-500/10'
                    : 'border-surface-700 bg-surface-800/50 hover:border-surface-600'
                )}
              >
                <Building2 className={cn(
                  'w-10 h-10 mb-3',
                  saleType === 'PLATFORM' ? 'text-primary-400' : 'text-surface-500'
                )} />
                <h3 className="text-lg font-semibold text-surface-100 mb-1">
                  Venda Rápida
                </h3>
                <p className="text-sm text-surface-400">
                  Venda instantânea para a plataforma com cotação atual
                </p>
                <Badge variant="success" size="sm" className="mt-3">
                  Instantâneo
                </Badge>
              </button>

              <button
                onClick={() => setSaleType('P2P')}
                className={cn(
                  'p-6 rounded-xl border text-left transition-all',
                  saleType === 'P2P'
                    ? 'border-primary-500 bg-primary-500/10'
                    : 'border-surface-700 bg-surface-800/50 hover:border-surface-600'
                )}
              >
                <Users className={cn(
                  'w-10 h-10 mb-3',
                  saleType === 'P2P' ? 'text-primary-400' : 'text-surface-500'
                )} />
                <h3 className="text-lg font-semibold text-surface-100 mb-1">
                  Venda P2P
                </h3>
                <p className="text-sm text-surface-400">
                  Defina seu preço e venda para outros usuários
                </p>
                <Badge variant="info" size="sm" className="mt-3">
                  Melhor preço
                </Badge>
              </button>
            </div>

            <div className="flex justify-end gap-3">
              <Button variant="ghost" onClick={onClose}>Cancelar</Button>
              <Button onClick={() => setStep('amount')}>
                Continuar
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </motion.div>
        )}

        {/* Step 2: Select Wallet and Amount */}
        {step === 'amount' && (
          <motion.div
            key="amount"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div>
              <label className="block text-sm font-medium text-surface-300 mb-2">
                Selecione a carteira
              </label>
              {wallets.filter(w => w.balance > 0).length > 0 ? (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {wallets.filter(w => w.balance > 0).map((wallet) => (
                    <button
                      key={wallet.id}
                      onClick={() => setSelectedWallet(wallet.id)}
                      className={cn(
                        'w-full p-4 rounded-lg border text-left transition-all',
                        selectedWallet === wallet.id
                          ? 'border-primary-500 bg-primary-500/10'
                          : 'border-surface-700 bg-surface-800/50 hover:border-surface-600'
                      )}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Wallet className="w-5 h-5 text-primary-400" />
                          <div>
                            <p className="text-surface-100 font-medium">{wallet.network}</p>
                            <p className="text-sm text-surface-500">
                              Saldo: {wallet.balance.toFixed(8)}
                            </p>
                          </div>
                        </div>
                        {selectedWallet === wallet.id && (
                          <CheckCircle className="w-5 h-5 text-primary-400" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="p-4 rounded-lg bg-yellow-500/10 border border-yellow-500/30 text-center">
                  <AlertCircle className="w-8 h-8 mx-auto mb-2 text-yellow-400" />
                  <p className="text-surface-300">Nenhuma carteira com saldo</p>
                </div>
              )}
            </div>

            {selectedWalletData && (
              <>
                <Input
                  label={`Quantidade de ${selectedWalletData.network}`}
                  type="number"
                  placeholder="0.00000000"
                  value={amountCrypto}
                  onChange={(e) => setAmountCrypto(e.target.value)}
                  min="0.00000001"
                  max={selectedWalletData.balance.toString()}
                  step="0.00000001"
                />

                <div className="flex gap-2">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setAmountCrypto((selectedWalletData.balance * 0.25).toFixed(8))}
                  >
                    25%
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setAmountCrypto((selectedWalletData.balance * 0.5).toFixed(8))}
                  >
                    50%
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setAmountCrypto((selectedWalletData.balance * 0.75).toFixed(8))}
                  >
                    75%
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => setAmountCrypto(selectedWalletData.balance.toFixed(8))}
                  >
                    100%
                  </Button>
                </div>

                {saleType === 'P2P' && (
                  <Input
                    label="Preço por unidade (R$)"
                    type="number"
                    placeholder={currentQuote ? currentQuote.priceBrl.toFixed(2) : '0.00'}
                    value={pricePerUnit}
                    onChange={(e) => setPricePerUnit(e.target.value)}
                    min="0.01"
                    step="0.01"
                  />
                )}

                {/* Estimated value */}
                <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/30">
                  <p className="text-surface-400 text-sm">Você receberá aproximadamente:</p>
                  <p className="text-2xl font-bold text-green-400">
                    {formatCurrency(saleType === 'PLATFORM' ? estimatedBrl : p2pTotalBrl)}
                  </p>
                  {saleType === 'PLATFORM' && (
                    <p className="text-xs text-surface-500 mt-1">
                      * Inclui spread de {SPREAD_PERCENTAGE}%
                    </p>
                  )}
                </div>
              </>
            )}

            <div className="flex justify-between gap-3">
              <Button variant="ghost" onClick={() => setStep('type')}>Voltar</Button>
              <Button 
                onClick={() => setStep('confirm')} 
                disabled={!selectedWallet || !amountCrypto || parseFloat(amountCrypto) <= 0 || (saleType === 'P2P' && !pricePerUnit)}
              >
                Continuar
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </motion.div>
        )}

        {/* Step 3: Confirm */}
        {step === 'confirm' && selectedWalletData && (
          <motion.div
            key="confirm"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <Card className="bg-surface-800/50">
              <h3 className="text-lg font-semibold text-surface-100 mb-4">Resumo da Venda</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-surface-400">Tipo</span>
                  <Badge variant={saleType === 'PLATFORM' ? 'success' : 'info'}>
                    {saleType === 'PLATFORM' ? 'Venda Rápida' : 'Venda P2P'}
                  </Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-surface-400">Quantidade</span>
                  <span className="text-surface-100 font-medium">
                    {amountCrypto} {selectedWalletData.network}
                  </span>
                </div>
                {saleType === 'P2P' && (
                  <div className="flex justify-between">
                    <span className="text-surface-400">Preço por unidade</span>
                    <span className="text-surface-100 font-medium">
                      {formatCurrency(parseFloat(pricePerUnit))}
                    </span>
                  </div>
                )}
                <div className="border-t border-surface-700 pt-3 flex justify-between">
                  <span className="text-surface-300 font-medium">Você receberá</span>
                  <span className="text-green-400 font-bold text-xl">
                    {formatCurrency(saleType === 'PLATFORM' ? estimatedBrl : p2pTotalBrl)}
                  </span>
                </div>
              </div>
            </Card>

            {saleType === 'P2P' && (
              <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/30">
                <p className="text-blue-400 text-sm">
                  ℹ️ Sua oferta será publicada no marketplace. Quando um comprador aceitar, 
                  você receberá o valor em sua conta virtual.
                </p>
              </div>
            )}

            <div className="flex justify-between gap-3">
              <Button variant="ghost" onClick={() => setStep('amount')}>Voltar</Button>
              <Button 
                onClick={handleConfirmSale} 
                disabled={createSale.isPending}
              >
                {createSale.isPending ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Processando...
                  </>
                ) : (
                  saleType === 'PLATFORM' ? 'Confirmar Venda' : 'Publicar Oferta'
                )}
              </Button>
            </div>
          </motion.div>
        )}

        {/* Step 4: Success */}
        {step === 'success' && saleResult && (
          <motion.div
            key="success"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-6"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', delay: 0.2 }}
              className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-500/20 flex items-center justify-center"
            >
              <CheckCircle className="w-10 h-10 text-green-400" />
            </motion.div>

            <h3 className="text-2xl font-bold text-surface-100 mb-2">
              {saleType === 'PLATFORM' ? 'Venda Realizada!' : 'Oferta Publicada!'}
            </h3>
            <p className="text-surface-400 mb-6">
              {saleType === 'PLATFORM' 
                ? 'Sua venda foi processada e o valor foi creditado em sua conta.'
                : 'Sua oferta está disponível no marketplace P2P.'}
            </p>

            <Card className="bg-surface-800/50 text-left mb-6">
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-surface-400">Quantidade</span>
                  <span className="text-surface-100">
                    {saleResult.amountCrypto} {saleResult.cryptoSymbol}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-surface-400">Valor</span>
                  <span className="text-green-400 font-bold">
                    {formatCurrency(saleResult.amountBrl / 100)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-surface-400">Status</span>
                  <Badge variant={saleResult.status === 'COMPLETED' ? 'success' : 'warning'}>
                    {saleResult.status}
                  </Badge>
                </div>
              </div>
            </Card>

            <Button onClick={onClose} className="w-full">
              Fechar
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </Modal>
  );
}

export default CryptoSellModal;
